import React from 'react';
import styled from 'styled-components';
import { Tuple } from '^/components/molecules/MyPage/Tuple';
import Text from './text';
import { useL10n } from '^/hooks';
import { GroupedSection } from '^/components/atoms/GroupedSection';
import { useParams } from 'react-router-dom';
import { useGetProjectDetail } from '^/hooks/projects/useGetProjectDetail';
import theme from '^/theme';
import CameraIcon from '^/assets/icons/camera.svg';
import { useSelector } from 'react-redux';
import * as T from '^/types';
import { usePopupStore } from '^/store/popup';

const Root = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  paddingInline: '60px',
  paddingTop: '22px',
});

const ImageUploadArea = styled.div({
  width: '100%',
  height: '200px',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  overflow: 'hidden',
  border: `1px solid ${theme.colorTokens.lineCool}`,
  backgroundColor: theme.colorTokens.bgLightCoolGray,
  marginTop: '8px',

  '&:hover': {
    borderColor: theme.colorTokens.lineCool,
    backgroundColor: theme.colorTokens.bgLightGray,
  },
});

const LogoUploadArea = styled(ImageUploadArea)({
  height: '100px',
  width: '100px',
  marginTop: '12px',
});

const ImagePreview = styled.img({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '8px',
});

const LogoPreview = styled(ImagePreview)({
  borderRadius: '4px',
});

const Wrapper = styled.div({
  marginTop: '12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '150%',
});

const ProjectInformation = () => {
  const [l10n] = useL10n();
  const params = useParams();
  const projectId = params.id;

  const { projectById } = useSelector((s: T.State) => ({
    projectById: s.Projects.projects.byId,
  }));

  const setPopupOptions = usePopupStore(s => s.setPopupOptions);
  const setMinorPopup = usePopupStore(s => s.setMinorPopup);

  const project = projectById[Number(projectId)];
  const hasProject = Boolean(project);

  useGetProjectDetail({ projectId, hasProject });

  const onTupleClickHandler = (type: T.ManageProjectPopupType) => {
    setPopupOptions({ manageProjectPopupType: type });
    setMinorPopup(T.ContentPageMinorPopupType.MANAGE_PROJECT_POPUP);
  };

  if (!project) {
    return null;
  }

  return (
    <Root>
      <GroupedSection title={l10n(Text.overview)}>
        <Tuple
          data-testid="project-information-title-tuple"
          title={l10n(Text.title)}
          value={project?.title}
          boldValue={true}
          nonEditable={false}
          onClick={() => onTupleClickHandler(T.ManageProjectPopupType.EDIT_PROJECT_TITLE)}
        />
        <Tuple
          data-testid="project-information-description-tuple"
          boldValue={true}
          value={project?.description}
          title={l10n(Text.description)}
          onClick={() => onTupleClickHandler(T.ManageProjectPopupType.EDIT_PROJECT_DESCRIPTION)}
        />
      </GroupedSection>
      <GroupedSection title={l10n(Text.appearance)} topBorder={true}>
        <Wrapper>{l10n(Text.projectLogo)}</Wrapper>
        <LogoUploadArea
          onClick={() => onTupleClickHandler(T.ManageProjectPopupType.EDIT_PROJECT_LOGO)}
        >
          {project?.logo ? <LogoPreview src={project?.logo} alt="Logo preview" /> : <CameraIcon />}
        </LogoUploadArea>
      </GroupedSection>
    </Root>
  );
};

export default ProjectInformation;
